import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell, GoogleButton } from "@/pages/Login";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      navigate("/products/new");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Free assessment and indicative scores. Pay only to unlock a detailed report."
      footer={
        <>
          Already registered?{" "}
          <Link to="/login" className="font-medium text-navy-800 underline underline-offset-4" data-testid="link-login">
            Log in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name" className="text-xs">Full name</Label>
          <Input
            id="name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name-input"
          />
        </div>
        <div>
          <Label htmlFor="email" className="text-xs">Work email</Label>
          <Input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email-input"
          />
        </div>
        <div>
          <Label htmlFor="company" className="text-xs">Company</Label>
          <Input
            id="company"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            data-testid="register-company-input"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs">Password (minimum 8 characters)</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password-input"
          />
        </div>
        {error && (
          <p className="border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="register-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="w-full bg-navy-800 text-white hover:bg-navy-700"
          data-testid="register-submit-button"
        >
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Sign up with Google" />
    </AuthShell>
  );
}
